import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useLocale } from '../contexts/LocaleContext'
import { getCityUrl, getDirectAffiliateLink, getBaseSlugFromUrl, siteConfig } from '../config/site'
import { updateSEO } from '../utils/seo'
import { loadArticle } from '../utils/loadArticle'
import { markdownToHtml } from '../utils/markdownToHtml'
import { isCityActive } from '../utils/activeCities'
import citiesData from '../data/cities.json'
import './CityPage.css'

const CityPage = () => {
  const { citySlug } = useParams()
  const navigate = useNavigate()
  const { t, locale } = useLocale()
  const [city, setCity] = useState(null)
  const [article, setArticle] = useState(null)
  const [loading, setLoading] = useState(true)
  
  // Memoize markdown conversions to avoid re-processing on every render
  const memoizedMarkdown = useMemo(() => {
    if (!article) return {}
    
    const renderStartTime = performance.now()
    const result = {}
    
    const sections = ['introduction', 'guide', 'places', 'tips', 'casualEncounters', 'discreteEncounters', 'availableNow']
    sections.forEach(section => {
      if (article[section]) {
        try {
          result[section] = markdownToHtml(article[section])
        } catch (error) {
          console.error(`Error parsing markdown for ${section}:`, error)
          // Fallback: return plain text if parsing fails
          result[section] = `<p>${String(article[section]).replace(/\n/g, '<br>')}</p>`
        }
      }
    })
    
    // Process FAQ answers
    if (article.faq && article.faq.items) {
      result.faq = article.faq.items.map(item => {
        try {
          return {
            ...item,
            answer: item.answer ? markdownToHtml(item.answer) : ''
          }
        } catch (error) {
          console.error('Error parsing FAQ answer:', error)
          return {
            ...item,
            answer: item.answer ? `<p>${String(item.answer).replace(/\n/g, '<br>')}</p>` : ''
          }
        }
      })
    }
    
    const renderDuration = performance.now() - renderStartTime
    console.log(`🔄 Memoized markdown conversion: ${renderDuration.toFixed(2)}ms`)
    
    return result
  }, [article])

  // Get locale string for number formatting
  const getNumberLocale = useCallback(() => {
    const localeMap = { 'de': 'de-DE', 'fr': 'fr-FR', 'en': 'en-US' }
    return localeMap[locale] || 'en-US'
  }, [locale])

  // Smooth scroll to section
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  useEffect(() => {
    // Performance debugging
    const pageStartTime = performance.now()
    console.log(`🔍 CityPage useEffect triggered for slug: ${citySlug}`)
    
    // Extract base slug from URL (remove prefix)
    const baseSlug = getBaseSlugFromUrl(citySlug)
    console.log(`📍 Extracted base slug: ${baseSlug}, prefix: ${siteConfig.citySlugPrefix}`)
    
    // Find city by base slug
    const foundCity = citiesData.find(
      (c) => c.Slug.toLowerCase() === baseSlug?.toLowerCase()
    )
    
    // Check if city exists and is active (if active cities are configured)
    if (foundCity && isCityActive(foundCity.Slug)) {
      setCity(foundCity)
      
      // Load article data
      const fetchArticle = async () => {
        setLoading(true)
        const loadStartTime = performance.now()
        const articleData = await loadArticle(foundCity.Slug, locale, 'city')
        const loadDuration = performance.now() - loadStartTime
        console.log(`📄 Article loaded in ${loadDuration.toFixed(2)}ms`)
        
        setArticle(articleData)
        
        // Update SEO meta tags
        if (articleData?.metaTitle) {
          updateSEO({
            title: articleData.metaTitle,
            description: articleData.metaDescription,
            keywords: articleData.metaKeywords,
            url: getCityUrl(foundCity.Slug)
          })
        } else {
          // Use default SEO
            updateSEO({
              title: t('seo.cityTitle', { city: foundCity.City }),
              description: t('seo.cityDescription', { 
                city: foundCity.City, 
                population: foundCity.Population.toLocaleString(getNumberLocale()) 
              }),
            keywords: t('seo.cityKeywords', { city: foundCity.City }),
            url: getCityUrl(foundCity.Slug)
          })
        }
        const pageDuration = performance.now() - pageStartTime
        console.log(`⏱️ Total CityPage load + render time: ${pageDuration.toFixed(2)}ms`)
        setLoading(false)
      }
      
      fetchArticle()
    } else {
      console.warn(`⚠️ City not found or inactive: ${baseSlug}`)
      // City not found, redirect to home
      navigate('/', { replace: true })
    }
    // Only depend on citySlug and locale - remove t and getNumberLocale to prevent re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug, locale])

  if (!city || loading) {
    return (
      <div className="city-page-container">
        <div className="loading">{t('common.loading')}</div>
      </div>
    )
  }

  return (
    <div className="city-page-container">
      <button className="back-button" onClick={() => navigate('/')}>
        ← {t('city.backToHome')}
      </button>

      <header className="city-header">
        <div className="city-header-content">
          <div className="city-badge">
            <span className="city-badge-icon">📍</span>
            <span>{city.City}</span>
          </div>
          <h1 className="city-page-title">
            {article?.title || t('city.welcome', { city: city.City })}
          </h1>
          <div className="city-stats">
            <div className="city-stat">
              <span className="city-stat-icon">👥</span>
              <span className="city-stat-value">{city.Population.toLocaleString(getNumberLocale())}</span>
              <span className="city-stat-label">{t('common.population')}</span>
            </div>
            <div className="city-stat">
              <span className="city-stat-icon">💕</span>
              <span className="city-stat-value">{t('city.activeCommunity')}</span>
              <span className="city-stat-label">{t('city.communityLabel')}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="city-content">
        {article ? (
          <>
            {/* Hero Image */}
            <div className="city-hero-image-wrapper">
              <img 
                src="/images/city-hero.png" 
                alt={t('city.heroImageAlt', { city: city.City })}
                className="city-hero-image"
                loading="eager"
                onError={(e) => {
                  // Fallback: hide image if it doesn't exist yet
                  e.target.style.display = 'none';
                }}
              />
            </div>

            {/* Table of Contents */}
            {(article.guide || article.places || article.tips || article.faq) && (
              <div className="table-of-contents">
                <h3 className="toc-title">{t('city.tableOfContents')}</h3>
                <ul className="toc-list">
                  {article.introduction && (
                    <li><a href="#introduction" onClick={(e) => { e.preventDefault(); scrollToSection('introduction') }}>{t('city.tocIntroduction')}</a></li>
                  )}
                  {article.guide && (
                    <li><a href="#guide" onClick={(e) => { e.preventDefault(); scrollToSection('guide') }}>{t('city.tocGuide')}</a></li>
                  )}
                  {article.places && (
                    <li><a href="#places" onClick={(e) => { e.preventDefault(); scrollToSection('places') }}>{t('city.tocPlaces')}</a></li>
                  )}
                  {article.tips && (
                    <li><a href="#tips" onClick={(e) => { e.preventDefault(); scrollToSection('tips') }}>{t('city.tocTips')}</a></li>
                  )}
                  {article.faq && (
                    <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq') }}>{t('city.tocFaq')}</a></li>
                  )}
                </ul>
              </div>
            )}

            {/* Introduction Section */}
            {memoizedMarkdown.introduction && (
              <div id="introduction" className="article-section introduction-section">
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.introduction }} />
              </div>
            )}

            {/* Legacy content field (for backward compatibility) */}
            {!article.introduction && article.content && (
              <div 
                className="article-section"
                dangerouslySetInnerHTML={{ __html: markdownToHtml(article.content) }}
              />
            )}

            {/* Guide Section */}
            {memoizedMarkdown.guide && (
              <div id="guide" className="article-section guide-section">
                <div className="section-header">
                  <span className="section-icon">📖</span>
                  <h2>{t('city.sectionGuideTitle', { city: city.City })}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.guide }} />
              </div>
            )}

            {/* Best Places Section */}
            {memoizedMarkdown.places && (
              <div id="places" className="article-section places-section">
                <div className="section-header">
                  <span className="section-icon">🗺️</span>
                  <h2>{t('city.sectionPlacesTitle', { city: city.City })}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.places }} />
              </div>
            )}

            {/* Tips Section */}
            {memoizedMarkdown.tips && (
              <div id="tips" className="article-section tips-section">
                <div className="section-header">
                  <span className="section-icon">💡</span>
                  <h2>{t('city.sectionTipsTitle', { city: city.City })}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.tips }} />
              </div>
            )}

            {/* Casual Encounters Section */}
            {memoizedMarkdown.casualEncounters && (
              <div id="casual-encounters" className="article-section casual-encounters-section">
                <div className="section-header">
                  <span className="section-icon">⚡</span>
                  <h2>{t('city.sectionCasualEncountersTitle', { city: city.City }) || 'Rencontres d\'un soir'}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.casualEncounters }} />
              </div>
            )}

            {/* Discrete Encounters Section */}
            {memoizedMarkdown.discreteEncounters && (
              <div id="discrete-encounters" className="article-section discrete-encounters-section">
                <div className="section-header">
                  <span className="section-icon">🔒</span>
                  <h2>{t('city.sectionDiscreteEncountersTitle', { city: city.City })}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.discreteEncounters }} />
              </div>
            )}

            {/* Available Now Section */}
            {memoizedMarkdown.availableNow && (
              <div id="available-now" className="article-section available-now-section">
                <div className="section-header">
                  <span className="section-icon">🕐</span>
                  <h2>{t('city.sectionAvailableNowTitle', { city: city.City })}</h2>
                </div>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: memoizedMarkdown.availableNow }} />
              </div>
            )}

            {/* FAQ Section */}
            {article.faq && (
              <div id="faq" className="article-section faq-section">
                <div className="section-header">
                  <span className="section-icon">❓</span>
                  <h2>{article.faq.title || t('city.sectionFaqTitle', { city: city.City })}</h2>
                </div>
                <div className="faq-list">
                  {(memoizedMarkdown.faq || article.faq.items || article.faq.questions || []).map((item, i) => (
                    <div key={i} className="faq-item">
                      <h3 className="faq-question">{item.question}</h3>
                      <div className="faq-answer" dangerouslySetInnerHTML={{ __html: item.answer || '' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Affiliate Link CTA */}
            <div className="affiliate-cta">
              <a 
                href={getDirectAffiliateLink()} 
                target="_blank" 
                rel="noopener noreferrer"
                className="affiliate-button"
              >
                {t('city.affiliateCTA', { city: city.City })}
              </a>
            </div>
          </>
        ) : (
          <>
            <p className="city-description">
              {t('city.description', { 
                city: city.City, 
                population: city.Population.toLocaleString(getNumberLocale()) 
              })}
            </p>
          </>
        )}
      </div>
    </div>
  )
}

export default CityPage
